import CloseIcon from "@mui/icons-material/Close";
import PublishIcon from "@mui/icons-material/Publish";
import { Autocomplete, Checkbox, MenuItem, Select, TextField } from "@mui/material";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { Field, FieldArray, Form, Formik } from "formik";
import { ReactElement, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import CustomButtonSimple from "../../../../components/CustomMiscellaneous/CustomButtonSimple";
import { CustomErrorMessage } from "../../../../components/FormComponents/CustomErrorMessage";
import { FormFieldWrapper } from "../../../../components/FormComponents/FieldWrapper";
import { FormFieldWrapperColumn } from "../../../../components/FormComponents/FieldWrapperColumn/FieldWrapperColumn";
import { hasError } from "../../../../components/FormComponents/utils";
import CustomDatePicker from "../../../../form/CustomInput/CustomDatePicker/CustomDatePicker";
import { genericGet, genericPatch, genericPost } from "../../../../services/api.service";
import { addDays } from "../../../../utils/DateUtils";
import { IClient } from "../../../Obras/newObra/IObra";
import { ImportanciaOrcamento, IOrcamento } from "../IOrcamento";
import { OrcamentacaoPecasForm } from "./OrcamentacaoPecasForm";
import { ServicosComplementaresForm } from "./ServicosComplementaresForm";
import { ValorSemIvaForm } from "./ValorSemIvaForm";

export default function OrcamentosForm({ orcamento, onCancel, refetch }: { orcamento: any; onCancel?: Function; refetch?: () => void }): ReactElement {
	const location = useLocation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const pedidoId = searchParams.get("pedidoId");

	const formRef = useRef<any>(null);
	let boundArrayHelpers;
	const [submitDisabled, setSubmitDisabled] = useState(false);

	const [pecasUpdatedIncrementer, setPecasUpdatedIncrementer] = useState(0);

	const locallySavedOrcamento = localStorage.getItem("orcamentosForm") ? JSON.parse(localStorage.getItem("orcamentosForm") || "") : null;

	const isEditingExisting = orcamento?.id || locallySavedOrcamento?.id;
	const successMessage = isEditingExisting ? "Orçamento atualizado com sucesso" : "Orçamento criado com sucesso";
	const errorMessage = isEditingExisting ? "Erro ao atualizar orçamento" : "Erro ao criar orçamento";

	const bindArrayHelpers = (arrayHelpers: any) => {
		boundArrayHelpers = arrayHelpers;
	};

	const saveCurrentState = (value: any) => {
		localStorage.setItem("orcamentosForm", JSON.stringify(value));
		toast.success("Formulário guardado localmente!");
	};

	const { data: clients = [] } = useQuery<IClient[]>({
		queryKey: ["clients"],
		queryFn: async () => {
			return await genericGet("/findByFilter/clientes");
		},
	});

	useQuery({
		queryKey: ["autoBillCode", orcamento],
		queryFn: async () => {
			return await genericGet("/orcamentos/generate/current-number");
		},
		onSuccess: (resp) => {
			console.log("Will ser billCode: ", resp.currentNumber);
			if (!formRef.current.values.billCode) {
				formRef.current.setFieldValue("billCode", resp.currentNumber);
				formRef.current.setFieldTouched("billCode", true);
			}
		},
	});

	const { data: materiais } = useQuery({
		queryKey: ["materiais"],
		queryFn: async () => {
			return (await genericGet("/materiais?page=1&take=100000")).data;
		},
	});

	const formInitialValues: Partial<IOrcamento> = useMemo(
		() =>
			locallySavedOrcamento || {
				id: orcamento?.id,
				name: orcamento?.name,
				client: orcamento?.client,
				valueWithoutIVA: orcamento?.valueWithoutIVA ?? 0,
				discardIVA: orcamento?.discardIVA ?? false,
				transporte: orcamento?.transporte ?? false,
				billCode: orcamento?.billCode ? orcamento?.billCode : "",
				observacoes: orcamento?.observacoes,
				prazoDeEntrega: orcamento?.prazoDeEntrega,
				vossaRequisicao: orcamento?.vossaRequisicao,
				importancia: orcamento?.importancia ?? ImportanciaOrcamento.ALGUMA_IMPORTANCIA,
				date: orcamento?.date ? orcamento?.date : new Date(),
				validade: orcamento?.validade ? orcamento?.validade : addDays(new Date(), 15),
				accepted: orcamento?.accepted ? orcamento.accepted : false,
				obras: orcamento?.obras,
				pecasOrcamentadas: orcamento?.pecasOrcamentadas || [],
				servicosComplementares: orcamento?.servicosComplementares || null,
				pedidoOrcamentoId: pedidoId ? parseInt(pedidoId) : null,
			},
		[orcamento]
	);

	const mutationMethod = (): MutationFunction<AxiosResponse<any, any>, any> => {
		if (!isEditingExisting) return genericPost("/orcamentos");
		return genericPatch(`/orcamentos/${isEditingExisting}`);
	};

	const mutation = useMutation(mutationMethod(), {
		onSuccess: () => {
			toast.success(successMessage);
			localStorage.removeItem("orcamentosForm");
			setSubmitDisabled(false);
			if (refetch) refetch();
			onCancel ? onCancel() : navigate(-1);
		},
		onError: () => {
			toast.error(errorMessage);
			setSubmitDisabled(false);
			console.log("ERR!!!");
		},
	});

	return (
		<div className="border rounded-md shadow-md p-8 px-16 text-sm m-10">
			<Formik
				initialValues={{ ...formInitialValues }}
				onSubmit={(values) => {
					setSubmitDisabled(true);
					mutation.mutate(values);
				}}
				innerRef={formRef}
			>
				{(props) => {
					const { values, errors, touched, handleChange, setFieldValue } = props;

					return (
						<Form>
							<div className="flex">
								<FormFieldWrapperColumn label={"Cliente"} className={"w-1/2"}>
									<Field
										className="w-full"
										name="client"
										onChange={(e: React.ChangeEvent<HTMLInputElement>, newValue: any) => {
											setFieldValue("client", newValue);
										}}
										value={values.client}
										as={Autocomplete}
										variant={"outlined"}
										label={"Cliente"}
										options={clients}
										getOptionLabel={(client: IClient) => client.name || ""}
										renderInput={(params: any) => <TextField {...params} label={"Cliente"} placeholder={"Cliente"} />}
									/>
								</FormFieldWrapperColumn>

								<FormFieldWrapperColumn label={"Nome do cliente"} className={"w-1/2"}>
									<>
										<Field className="w-full" name="name" as={TextField} error={hasError({ errors, touched, name: "name" })} variant={"outlined"} type={"text"} label={"Nome do cliente"} />
										<CustomErrorMessage name={"name"} />
									</>
								</FormFieldWrapperColumn>
							</div>

							<div className="flex">
								<FormFieldWrapperColumn label={"Número do orçamento"} className="w-1/2">
									<>
										<Field className="w-full" name="billCode" as={TextField} error={hasError({ errors, touched, name: "billCode" })} variant={"outlined"} type={"text"} label={"Número do orçamento"} />
										<CustomErrorMessage name={"billCode"} />
									</>
								</FormFieldWrapperColumn>

								<FormFieldWrapperColumn label={"Observações"} className="w-1/2">
									<>
										<Field className="w-full" name="observacoes" as={TextField} error={hasError({ errors, touched, name: "observacoes" })} variant={"outlined"} type={"text"} label={"Observaçoes"} />
										<CustomErrorMessage name={"observacoes"} />
									</>
								</FormFieldWrapperColumn>
							</div>
							<div className="flex items-center">
								<FormFieldWrapperColumn label={"V/Requisição"} className="w-1/2">
									<>
										<Field className="w-full" name="vossaRequisicao" as={TextField} error={hasError({ errors, touched, name: "vossaRequisicao" })} variant={"outlined"} type={"text"} label={"V/Requisição"} />
										<CustomErrorMessage name={"vossaRequisicao"} />
									</>
								</FormFieldWrapperColumn>

								<FormFieldWrapperColumn label={"Prazo de entrega"} className="w-1/2">
									<>
										<Field className="w-full" name="prazoDeEntrega" as={TextField} error={hasError({ errors, touched, name: "prazoDeEntrega" })} variant={"outlined"} type={"text"} label={"Prazo de Entrega"} />
										<CustomErrorMessage name={"prazoDeEntrega"} />
									</>
								</FormFieldWrapperColumn>

								<FormFieldWrapperColumn label={"Importância"} className={"w-1/2"}>
									<Field className="w-full" name="importancia" value={values.importancia} as={Select} variant={"outlined"} label={"Importância"}>
										{Object.values(ImportanciaOrcamento).map((s: string) => (
											<MenuItem key={s} value={s}>
												{s}
											</MenuItem>
										))}
									</Field>
								</FormFieldWrapperColumn>
							</div>

							<div className="flex items-center">
								<FormFieldWrapperColumn label={"Data de expedição"} className="w-1/3">
									<>
										<Field
											className="w-full bg-zinc-300"
											name={`date`}
											size="small"
											as={CustomDatePicker}
											onChange={(value: Date) => {
												setFieldValue(`date`, value);
											}}
											value={values.date ? values.date : new Date()}
											error={hasError({ errors, touched, name: "date" })}
											variant={"outlined"}
											label={"Data expedição"}
										/>
										<CustomErrorMessage name={`date`} />
									</>
								</FormFieldWrapperColumn>

								<FormFieldWrapperColumn label={"Validade"} className="w-1/3">
									<>
										<Field
											className="w-full bg-zinc-300"
											name={`validade`}
											size="small"
											as={CustomDatePicker}
											onChange={(value: Date) => {
												setFieldValue(`validade`, value);
											}}
											value={values.validade ? values.validade : addDays(new Date(), 15)}
											error={hasError({ errors, touched, name: "validade" })}
											variant={"outlined"}
											label={"Data expedição"}
										/>
										<CustomErrorMessage name={`validade`} />
									</>
								</FormFieldWrapperColumn>

								<FormFieldWrapperColumn label={"Aceite"}>
									<Field className="w-full" name={`accepted`} as={Checkbox} checked={values.accepted} error={hasError({ errors, touched, name: `accepted` })} variant={"outlined"} label={"Aceite"} />
								</FormFieldWrapperColumn>
							</div>

							<div>
								<FieldArray
									name={"pecasOrcamentadas"}
									render={(arrayHelpers) => {
										bindArrayHelpers(arrayHelpers);
										return <OrcamentacaoPecasForm setPecasUpdatedIncrementer={setPecasUpdatedIncrementer} saveCurrentState={saveCurrentState} materiais={materiais} formikProps={props} pecasArrayHelpers={arrayHelpers} />;
									}}
								/>
							</div>
							<FormFieldWrapper label={"Transporte"}>
								<Field className="w-full" name={`transporte`} as={Checkbox} checked={values.transporte} error={hasError({ errors, touched, name: `transporte` })} variant={"outlined"} label={"Aceite"} />
							</FormFieldWrapper>

							<ValorSemIvaForm pecasUpdatedIncrementer={pecasUpdatedIncrementer} />

							<FormFieldWrapper label={"Descartar IVA (orçamentos dados para o estrangeiro)"}>
								<Field className="w-full" name={`discardIVA`} as={Checkbox} checked={values.discardIVA} error={hasError({ errors, touched, name: `discardIVA` })} variant={"outlined"} label={"Descartar IVA"} />
							</FormFieldWrapper>

							<ServicosComplementaresForm />

							<div className="flex justify-between mt-3">
								<CustomButtonSimple className="mr-2 bg-cyan-600" text={"Guardar"} Icon={<CloseIcon />} onClick={() => saveCurrentState(values)} />

								<div className="flex justify-end mr-2">
									<CustomButtonSimple
										className="mr-2"
										text={"Cancelar"}
										Icon={<CloseIcon />}
										onClick={() => {
											localStorage.removeItem("orcamentosForm");
											onCancel ? onCancel(false) : navigate(-1);
										}}
									/>

									<CustomButtonSimple disabled={submitDisabled} type="submit" text={"Submeter"} Icon={<PublishIcon />} />
								</div>
							</div>
						</Form>
					);
				}}
			</Formik>
		</div>
	);
}
